import { TFunction } from 'i18next'
import {
  BooleanSchemaParams,
  MarkdownSchemaParams,
  NumberSchemaParams,
  StringSchemaParams,
  DateSchemaParams,
  NumberFromStringSchemaParams,
  ArraySchemaParams,
} from 'src/Configuration/validation/types'
import { boolean, number, NumberSchema, string, StringSchema, BooleanSchema, DateSchema, date, array, ArraySchema, AnyObject } from 'yup'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'
import { isNaN, isUndefined } from 'lodash'

/**
 * Helper schema for boolean values
 * @param t Translation-function
 * @param params BooleanSchemaParams → hasToBeTrue: boolean
 * @returns BooleanSchema that optionally tests for a true-value
 */
export const booleanSchema =
  (t: TFunction) =>
  (params: BooleanSchemaParams): BooleanSchema => {
    const { hasToBeTrue } = params || {}
    let response = boolean()

    if (hasToBeTrue) {
      response = response.oneOf([true], t(ValidationMessageKeys.required))
    }

    return response
  }

/**
 * Helper schema for string values
 * @param t Translation-function
 * @param params StringSchemaParams → minLength: number, maxLength: number, regexp: { rule: RegExp; errorMessage: string }
 * @returns StringSchema with optional minLength, maxLength and regexp validations
 */
export const stringSchema =
  (t: TFunction) =>
  (params: StringSchemaParams): StringSchema => {
    const { minLength, maxLength, regexp } = params || {}
    let response = string()

    if (minLength) {
      response = response.min(minLength, t(ValidationMessageKeys.lengthMin, { value: minLength }))
    }

    if (maxLength) {
      response = response.max(maxLength, t(ValidationMessageKeys.lengthMax, { value: maxLength }))
    }

    if (regexp) {
      response = response.matches(regexp.rule, t(regexp.errorMessage))
    }

    return response
  }

/**
 * Validates a stringSchema, if its a positive number
 * @param t Translation-function
 * @param min number
 * @param max number
 * @returns StringSchema that tests for a number and not negativ number
 */
export const numberFromStringSchema =
  (t: TFunction) =>
  (params?: NumberFromStringSchemaParams): StringSchema => {
    const { min, max, positive = true } = params || {}

    let response = string().test('isNumber', t(ValidationMessageKeys.number), value => {
      const number = Number(value)
      return !isNaN(number) || value === undefined
    })

    if (positive) {
      response = response.test('isNotNegativeNumber', t(ValidationMessageKeys.min, { value: 0 }), value => {
        const number = Number(value)
        return number >= 0 || value === undefined
      })
    }

    if (min) {
      response = response.test('isMinOutreached', t(ValidationMessageKeys.min, { value: min }), value => {
        const number = Number(value)
        return number >= min || value === undefined
      })
    }
    if (max) {
      response = response.test('isMaxOutreached', t(ValidationMessageKeys.max, { value: max }), value => {
        const number = Number(value)
        return number <= max || value === undefined
      })
    }
    return response
  }

/**
 * Helper schema for array values
 * @param t Translation-function
 * @param params ArraySchemaParams → minLength?: number, maxLength?: number, itemSchema?: Schema<T>
 * @returns a ArraySchema with optional minLength, maxLength validations and an itemSchema
 */
export const arraySchema =
  (t: TFunction) =>
  <T>(params: ArraySchemaParams<T>): ArraySchema<T[] | undefined, AnyObject, '', ''> => {
    const { minLength, maxLength, itemSchema } = params || {}
    let response = array().of(itemSchema) as ArraySchema<T[] | undefined, AnyObject, '', ''>

    if (minLength) {
      response = response.min(minLength, t(ValidationMessageKeys.arrayMin, { count: minLength }))
    }

    if (maxLength) {
      response = response.max(maxLength, t(ValidationMessageKeys.arrayMax, { count: maxLength }))
    }

    return response
  }

/**
 * Helper schema for date values
 * @param t Translation-function
 * @param params DateSchemaParams → minDate?: Date | number | string, maxDate?: Date | number | string
 * @returns a DateSchema with optional minDate and maxDate validations
 */
export const dateSchema =
  (t: TFunction) =>
  (params: DateSchemaParams): DateSchema => {
    const { minDate, maxDate } = params || {}
    let response = date()

    if (minDate) {
      response = response.min(minDate, t(ValidationMessageKeys.minDate, { value: minDate }))
    }

    if (maxDate) {
      response = response.max(maxDate, t(ValidationMessageKeys.maxDate, { value: maxDate }))
    }

    return response
  }

/**
 * Helper schema for number values
 * @param t Translation-function
 * @param params NumberSchemaParams → min?: number, max?: number, integer?: boolean
 * @returns NumberSchema with optional min, max and integer validations
 */
export const numberSchema =
  (t: TFunction) =>
  (params: NumberSchemaParams): NumberSchema => {
    const { min, max, integer } = params || {}
    let response = number()

    if (!isUndefined(min)) {
      response = response.min(min, t(ValidationMessageKeys.min, { value: min }))
    }

    if (!isUndefined(max)) {
      response = response.max(max, t(ValidationMessageKeys.max, { value: max }))
    }

    /**
     * This 'transform' call is needed, because inside NumberField (where we usually hold number inputs that we
     * validate) in case of an empty input, we set the value of the field to be NaN on first render. This triggers a
     * validation, which will fail, because NaN is not a valid number in yup
     * */
    response = response.transform(value => (isNaN(value) ? undefined : value))

    if (integer) {
      response = response.integer(t(ValidationMessageKeys.integer))
    }

    return response
  }

/**
 * Helper schema for markdown values
 * @param t Translation-function
 * @param params MarkdownSchemaParams → minLength: number, maxLength: number
 * @returns required StringSchema with minLength and maxLength validations
 */
export const markdownSchema =
  (t: TFunction) =>
  ({ minLength, maxLength }: MarkdownSchemaParams): StringSchema =>
    string()
      .transform(value => value.replaceAll('\n', '').trim())
      .min(minLength, t(ValidationMessageKeys.tooShort, { value: minLength }))
      .max(maxLength, t(ValidationMessageKeys.tooLong, { value: maxLength }))
      .required(t(ValidationMessageKeys.tooShort, { value: minLength }))
